// Écran « Mes niveaux » (lot 3) : overlay DOM listant les cartes du joueur connecté, avec
// les actions Modifier / Supprimer, et un bouton « Nouvelle carte ». Toutes les cartes sont
// visibles par la famille dès leur enregistrement (plus de brouillon ni de bouton Publier).
// Reprend l'esthétique de la carte d'authentification (public/index.html) ; ce module la
// pilote (chargement, rendu des lignes, actions, erreurs). L'éditeur et le jeu restent
// 100 % canvas ; ici on veut de VRAIS boutons HTML (accessibles au clic, au focus clavier).
//
// Sécurité (revue lot 3) : tout texte d'origine utilisateur (nom de carte) est inséré via
// textContent, JAMAIS innerHTML. Les noms de carte n'ont pas de filtre de charset côté
// serveur : un nom piégé ne doit jamais devenir du HTML (risque de XSS stocké). Les
// libellés fixes (« Modifier », « Supprimer »...) sont des chaînes statiques.
import type { LevelSummary } from '../shared/api-types'
import type { MonkSpeed } from '../shared/types'
import type { WorldKey } from '../shared/worlds'
import { TEST_LEVELS } from '../shared/levels-catalog'
import { apiDeleteLevel, apiDropOfficialLevel, apiGetLevel, apiMyLevels } from './api'
import { dropDraft, type LocalDraft, readDrafts } from './maps-offline'
import { chargerOverrides, estRetouchee } from './overrides'
import { errText, mkById } from './util'

export interface MyLevelsOpts {
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  // Ouvre l'éditeur : undefined = nouvelle carte ; id absent = COPIE à créer (carte officielle
  // du catalogue, 16/07 nuit) ; sinon la carte à modifier (grille incluse).
  onEdit: (level?: { id?: number; localId?: string; name: string; rows: string[]; world: WorldKey; monkSpeed: MonkSpeed; smartMonks: boolean; monkPowers: boolean; decor: string }) => void
  // Joue une carte pas encore publiée (créée hors ligne). Aucun score n'est envoyé : elle n'existe
  // pas encore côté serveur, il n'y a donc rien à quoi rattacher un temps.
  onPlayDraft: (level: { name: string; rows: string[]; world: WorldKey; monkSpeed: MonkSpeed; smartMonks: boolean; monkPowers: boolean; decor: string }) => void
  onQuit: () => void // retour au menu principal (Échap)
  // ADMIN (30/07) : le rôle du joueur. Seul 'admin' voit la section « Cartes officielles ».
  // ⚠️ Ce n'est PAS la sécurité — le serveur refuse l'écriture aux autres de toute façon. C'est du
  // rangement : la section n'a aucun sens pour un enfant.
  role?: string
  // Ouvre une carte OFFICIELLE dans l'éditeur, en mode surcharge (le slug est conservé).
  onEditOfficial?: (slug: string) => void
}
export interface MyLevelsHandle { stop(): void }

export function startMyLevels(opts: MyLevelsOpts): MyLevelsHandle {
  const byId = mkById('Mes niveaux')
  const root = byId('mylevels')
  const listEl = byId('ml-list')
  const errorEl = byId('ml-error')
  const newBtn = byId<HTMLButtonElement>('ml-new')
  const backBtn = byId<HTMLButtonElement>('ml-back')
  const offBloc = byId('ml-officiel')
  const offList = byId('ml-off-list')

  let stopped = false
  // Une seule action réseau à la fois : évite les doubles clics et les actions concurrentes
  // entre lignes pendant qu'un appel (modifier/supprimer) et le rechargement sont en vol.
  let busy = false

  // SÉCURITÉ : textContent (jamais innerHTML) pour les messages serveur, qui peuvent
  // contenir des caractères repris d'une saisie utilisateur.
  function showError(msg: string): void { errorEl.textContent = msg }
  // Ligne unique d'information dans la liste (chargement, liste vide, échec de chargement).
  function setMessage(text: string): void {
    listEl.replaceChildren()
    const p = document.createElement('p')
    p.className = 'ml-empty'
    p.textContent = text
    listEl.appendChild(p)
  }

  /** LA SECTION ADMIN : la liste des cartes officielles, chacune modifiable ou restaurable.
   *  Construite une fois à l'ouverture ; elle ne dépend que du catalogue embarqué. */
  async function monterOfficielles(): Promise<void> {
    // ⚠️ On REFERME d'abord, systématiquement. `logout()` ne recharge pas la page (main.ts) :
    // sur le Mac familial, un enfant qui se connecte après Claude-Alain héritait sinon d'une
    // section admin restée ouverte, avec ses 19 lignes et leurs boutons encore actifs. Le
    // serveur refusait bien l'écriture (403), mais l'enfant voyait une section qui n'est pas
    // pour lui et récoltait des erreurs.
    offList.replaceChildren()
    offBloc.hidden = true
    if (opts.role !== 'admin' || !opts.onEditOfficial) return
    offBloc.hidden = false
    await chargerOverrides()
    if (stopped) return
    offList.replaceChildren()
    for (const l of TEST_LEVELS) {
      const ligne = document.createElement('div')
      ligne.className = 'ml-row'
      const t = document.createElement('div')
      t.className = 'ml-name'
      t.textContent = l.title
      if (estRetouchee(l.slug)) {
        const b = document.createElement('span')
        b.className = 'ml-retouchee'
        b.textContent = ' · retouchée'
        t.appendChild(b)
      }
      const modif = document.createElement('button')
      modif.type = 'button'; modif.className = 'ml-act'; modif.textContent = 'Modifier'
      // ⚠️ teardown() AVANT d'ouvrir l'éditeur — comme TOUS les autres boutons de ce fichier.
      // Sans lui l'éditeur s'ouvrait bel et bien, mais DERRIÈRE : #mylevels et #editor sont tous
      // deux `position:fixed; inset:0; z-index:10` sur fond opaque, et #mylevels est déclaré plus
      // bas dans index.html, donc il peint par-dessus à z-index égal. Écran figé, clic sans effet.
      // Le garde `busy` compte autant : sans lui, quelqu'un qui reclique parce que « rien ne se
      // passe » empilait un éditeur par clic, chacun avec son écouteur clavier.
      modif.addEventListener('click', () => {
        if (busy) return
        teardown()
        opts.onEditOfficial!(l.slug)
      })
      ligne.append(t, modif)
      if (estRetouchee(l.slug)) {
        const rendre = document.createElement('button')
        rendre.type = 'button'; rendre.className = 'ml-act'; rendre.textContent = 'Rendre l\u2019originale'
        rendre.addEventListener('click', () => {
          if (busy) return
          busy = true
          apiDropOfficialLevel(l.slug)
            .then(() => monterOfficielles())
            .catch((e) => showError(e instanceof Error ? e.message : 'Impossible d\u2019annuler.'))
            .finally(() => { busy = false })
        })
        ligne.appendChild(rendre)
      }
      offList.appendChild(ligne)
    }
  }

  // Recharge la liste depuis le serveur et la reconstruit. Appelée à l'ouverture et après
  // chaque suppression pour refléter l'état réel côté serveur.
  async function refresh(): Promise<void> {
    showError('')
    setMessage('Chargement...')
    // BROUILLONS HORS LIGNE (25/07) : lus AVANT le réseau, et affichés même si le chargement des
    // cartes du serveur échoue. C'est la promesse faite à l'auteur : une carte créée au chalet est
    // toujours là, visible, jouable et modifiable, tant qu'elle n'a pas pu partir.
    const drafts = readDrafts(localStorage)
    let levels: LevelSummary[] = []
    let horsLigne = false
    try {
      levels = await apiMyLevels()
    } catch (e) {
      if (stopped) return
      horsLigne = true
      if (drafts.length === 0) {
        showError(errText(e))
        setMessage('Impossible de charger tes cartes. Réessaie.')
        return
      }
    }
    if (stopped) return
    listEl.replaceChildren()
    for (const d of drafts) listEl.appendChild(buildDraftRow(d))
    if (horsLigne) {
      const info = document.createElement('p')
      info.className = 'ml-empty'
      info.textContent = 'Hors ligne : seules tes cartes en attente de publication sont affichées.'
      listEl.appendChild(info)
    } else if (levels.length === 0 && drafts.length === 0) {
      setMessage('Aucune carte pour l\'instant. Clique sur « Nouvelle carte » pour en créer une.')
    }
    for (const lvl of levels) listEl.appendChild(buildRow(lvl))
    // Cartes OFFICIELLES (16/07 nuit, demande Claude-Alain : « je dois pouvoir modifier les
    // maps créées par toi ») : chaque carte du catalogue s'ouvre dans l'éditeur en COPIE —
    // l'original reste intact (records, campagnes), la version modifiée devient une carte perso.
    const head = document.createElement('p')
    head.className = 'ml-empty'
    head.textContent = '— Cartes officielles (s\'ouvrent en copie, l\'originale reste intacte) —'
    listEl.appendChild(head)
    for (const entry of TEST_LEVELS) listEl.appendChild(buildCatalogRow(entry))
  }

  /** Ligne d'un BROUILLON local (créé sans réseau, pas encore publié) : jouable et modifiable
   *  tout de suite ; il partira tout seul au retour du réseau. */
  function buildDraftRow(d: LocalDraft): HTMLElement {
    const row = document.createElement('div')
    row.className = 'ml-row'
    const mainCol = document.createElement('div')
    mainCol.className = 'ml-row-main'
    const name = document.createElement('span')
    name.className = 'ml-name'
    name.textContent = d.name
    mainCol.appendChild(name)
    const badges = document.createElement('span')
    badges.className = 'ml-badges'
    const wait = document.createElement('span')
    wait.className = 'ml-badge'
    wait.textContent = '⏳ à publier'
    badges.appendChild(wait)
    mainCol.appendChild(badges)
    row.appendChild(mainCol)

    const rows = d.ascii.split('\n')
    const actions = document.createElement('div')
    actions.className = 'ml-row-actions'
    const play = document.createElement('button')
    play.type = 'button'
    play.className = 'ml-act'
    play.textContent = 'Jouer'
    play.addEventListener('click', () => {
      if (busy) return
      teardown()
      opts.onPlayDraft({ name: d.name, rows, world: d.world, monkSpeed: d.monkSpeed, smartMonks: d.smartMonks, monkPowers: d.monkPowers, decor: d.decor })
    })
    const edit = document.createElement('button')
    edit.type = 'button'
    edit.className = 'ml-act'
    edit.textContent = 'Modifier'
    edit.addEventListener('click', () => {
      if (busy) return
      teardown()
      opts.onEdit({ localId: d.localId, name: d.name, rows, world: d.world, monkSpeed: d.monkSpeed, smartMonks: d.smartMonks, monkPowers: d.monkPowers, decor: d.decor })
    })
    const del = document.createElement('button')
    del.type = 'button'
    del.className = 'ml-act ml-act-danger'
    del.textContent = 'Supprimer'
    del.addEventListener('click', () => {
      if (busy) return
      if (!confirm(`Supprimer « ${d.name} » ? Elle n'a pas encore été publiée.`)) return
      dropDraft(localStorage, d.localId)
      void refresh()
    })
    actions.append(play, edit, del)
    row.appendChild(actions)
    return row
  }

  // Ligne d'une carte du CATALOGUE : nom + taille + « Copier & modifier ».
  function buildCatalogRow(entry: (typeof TEST_LEVELS)[number]): HTMLElement {
    const row = document.createElement('div')
    row.className = 'ml-row'
    const mainCol = document.createElement('div')
    mainCol.className = 'ml-row-main'
    const name = document.createElement('span')
    name.className = 'ml-name'
    name.textContent = entry.title
    mainCol.appendChild(name)
    const badges = document.createElement('span')
    badges.className = 'ml-badges'
    const size = document.createElement('span')
    size.className = 'ml-badge'
    size.textContent = `${entry.rows[0]!.length}\u00d7${entry.rows.length}`
    badges.appendChild(size)
    mainCol.appendChild(badges)
    row.appendChild(mainCol)
    const actions = document.createElement('div')
    actions.className = 'ml-row-actions'
    const copyBtn = document.createElement('button')
    copyBtn.type = 'button'
    copyBtn.className = 'ml-act'
    copyBtn.textContent = 'Copier & modifier'
    copyBtn.addEventListener('click', () => {
      if (busy) return
      teardown()
      opts.onEdit({ // PAS d'id : la sauvegarde CRÉERA une carte perso
        name: `${entry.title} (perso)`,
        rows: [...entry.rows],
        world: entry.world,
        monkSpeed: entry.monkSpeed ?? 'normal',
        smartMonks: entry.smartMonks ?? false,
        monkPowers: entry.monkPowers ?? false,
        decor: entry.decor ?? '',
      })
    })
    actions.appendChild(copyBtn)
    row.appendChild(actions)
    return row
  }

  // Construit une ligne : nom + badge (fini) + actions (Modifier, Supprimer).
  function buildRow(lvl: LevelSummary): HTMLElement {
    const row = document.createElement('div')
    row.className = 'ml-row'

    const mainCol = document.createElement('div')
    mainCol.className = 'ml-row-main'
    const name = document.createElement('span')
    name.className = 'ml-name'
    name.textContent = lvl.name // SÉCURITÉ : textContent (nom d'origine utilisateur)
    mainCol.appendChild(name)

    const badges = document.createElement('span')
    badges.className = 'ml-badges'
    if (lvl.authorFinished) {
      const done = document.createElement('span')
      done.className = 'ml-badge ml-badge-done'
      done.textContent = '✓ fini'
      badges.appendChild(done)
    }
    mainCol.appendChild(badges)
    row.appendChild(mainCol)

    const actions = document.createElement('div')
    actions.className = 'ml-row-actions'

    const editBtn = document.createElement('button')
    editBtn.type = 'button'
    editBtn.className = 'ml-act'
    editBtn.textContent = 'Modifier'
    editBtn.addEventListener('click', () => void onModify(lvl, editBtn))
    actions.appendChild(editBtn)

    const delBtn = document.createElement('button')
    delBtn.type = 'button'
    delBtn.className = 'ml-act ml-act-danger'
    delBtn.textContent = 'Supprimer'
    delBtn.addEventListener('click', () => void onDelete(lvl, delBtn))
    actions.appendChild(delBtn)

    row.appendChild(actions)
    return row
  }

  // Modifier : charge la grille complète (apiGetLevel) puis rend la main à l'éditeur. On
  // masque l'overlay AVANT d'ouvrir l'éditeur (les deux sont plein écran) ; main.ts rouvre
  // « Mes niveaux » à la sortie de l'éditeur.
  async function onModify(lvl: LevelSummary, btn: HTMLButtonElement): Promise<void> {
    if (busy) return
    busy = true
    showError('')
    btn.disabled = true
    try {
      const full = await apiGetLevel(lvl.id)
      if (stopped) return
      teardown()
      opts.onEdit({ id: full.id, name: full.name, rows: full.ascii.split('\n'), world: full.world, monkSpeed: full.monkSpeed, smartMonks: full.smartMonks, monkPowers: full.monkPowers, decor: full.decor })
    } catch (e) {
      if (stopped) return
      showError(errText(e))
      btn.disabled = false
    } finally {
      busy = false
    }
  }

  // Supprimer : confirmation native (demande du brief : action définitive) puis suppression.
  async function onDelete(lvl: LevelSummary, btn: HTMLButtonElement): Promise<void> {
    if (busy) return
    if (!confirm(`Supprimer la carte « ${lvl.name} » ? Cette action est définitive.`)) return
    busy = true
    showError('')
    btn.disabled = true
    try {
      await apiDeleteLevel(lvl.id)
      if (stopped) return
      await refresh()
    } catch (e) {
      if (stopped) return
      showError(errText(e))
      btn.disabled = false
    } finally {
      busy = false
    }
  }

  // « Nouvelle carte » : masque l'overlay et ouvre l'éditeur sur une carte vierge.
  function onNew(): void {
    if (busy) return
    teardown()
    opts.onEdit(undefined)
  }
  // « ← Retour » : seule sortie visible au doigt (aucun Échap sur smartphone), utile
  // aussi à la souris. Même chemin exactement que la touche Échap ci-dessous.
  function onBack(): void {
    teardown()
    opts.onQuit()
  }
  function onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape') { e.preventDefault(); teardown(); opts.onQuit() }
  }

  function teardown(): void {
    if (stopped) return
    stopped = true
    newBtn.removeEventListener('click', onNew)
    backBtn.removeEventListener('click', onBack)
    removeEventListener('keydown', onKey)
    listEl.replaceChildren()
    showError('')
    root.hidden = true
  }

  newBtn.addEventListener('click', onNew)
  backBtn.addEventListener('click', onBack)
  addEventListener('keydown', onKey)
  root.hidden = false
  void refresh()
  void monterOfficielles()   // section admin : ne fait rien pour les autres rôles
  return { stop: teardown }
}
